import { z } from 'zod'

/**
 * Who a customer or contact is in Thai tax terms: a juristic person (บริษัท ห้างหุ้นส่วน —
 * นิติบุคคล) or an individual (บุคคลธรรมดา). Only a juristic person withholds tax on the
 * freight it pays the company (หัก ณ ที่จ่าย); an individual pays it in full.
 */
export const partyTypes = ['juristic', 'individual'] as const
export type PartyType = (typeof partyTypes)[number]

/**
 * What line of business a sender or receiver is in, to see which trades the freight comes from
 * and goes to (shops by trade, chain stores, online sellers, people). Labels: `tms:businessType`
 */
export const businessTypes = [
  'construction',
  'agriculture',
  'grocery',
  'auto_parts',
  'electrical',
  'furniture',
  'apparel',
  'food_service',
  'industrial',
  'chain_store',
  'online_seller',
  'individual',
  'other',
] as const
export type BusinessType = (typeof businessTypes)[number]

/** Whether this party may withhold tax on what it pays us (anything but an individual) */
export function canWithhold(partyType: unknown): boolean {
  return partyType !== 'individual'
}

/** A juristic person's (or a government body's) 13-digit tax ID starts with 0; a person's ID card number with 1–8 */
export const isJuristicTaxId = (taxId: unknown): boolean => typeof taxId === 'string' && /^0\d{12}$/.test(taxId)

/**
 * A sender or receiver known to be a person, who does not withhold tax (tms.contact): its tax ID a person's ID card
 * number, or its business an individual's. One with no tax ID is not known either way: the 50 ทวิ's says
 */
export function isKnownIndividual(party: { taxId?: string | null; businessType?: string | null }): boolean {
  return party.businessType === 'individual' || (!!party.taxId && !isJuristicTaxId(party.taxId))
}

/** Thai banks a salary is paid into or a supplier is paid at (their usual short names). Labels: `banks` (common) */
export const banks = [
  'KBANK',
  'SCB',
  'BBL',
  'KTB',
  'BAY',
  'TTB',
  'GSB',
  'BAAC',
  'GHB',
  'UOB',
  'CIMB',
  'KKP',
  'LHB',
  'TISCO',
  'ICBC',
] as const

/** An account number at a Thai bank: digits only, 10–15 of them (most banks 10, savings banks 12) */
export const bankAccountDigits = z
  .string()
  .trim()
  .regex(/^\d{10,15}$/, 'เลขบัญชีต้องเป็นตัวเลข 10–15 หลัก ไม่มีขีด')
export const bankAccountNumber = bankAccountDigits.nullish()
